import { BRAND_SOURCE_KEY, BRAND_PERSON_NAME, BRAND_TELEGRAM_URL, BRAND_FAN_URL, BRAND_SITE_URL } from "@/lib/brandConfig";
/**
 * Bio Template Premium
 *
 * Template reutilizable y totalmente configurable via props.
 * Página standalone de suscripción mensual premium — sin invitation codes.
 *
 * Soporta dos monedas y sus gateways correspondientes:
 *   - ARS → Mercado Pago Subscriptions (preapproval mensual, redirect hosted)
 *   - USD → Stripe Checkout Subscription (mensual, redirect hosted)
 *         con source: BRAND_SOURCE_KEY en metadata
 *
 * Features:
 * - Planes Standard y VIP configurables (precio mensual, nombre, subtítulo)
 * - Gateway automático según moneda: ARS=MercadoPago / USD=Stripe
 * - Suscripción mensual real en ambos gateways
 * - Branding configurable (logo, nombre, país, idioma)
 * - **Promociones Especiales** (configurable via VITE_PREMIUM_SPECIAL_OFFER env var):
 *   
 *   Modo Normal (default):
 *   - Modal de recuperación al cerrar video VIP
 *   - Oferta única por usuario (tracking en DB)
 *   - Descuento solo si acepta explícitamente
 *   
 *   Modo Promoción Especial (VITE_PREMIUM_SPECIAL_OFFER=true):
 *   - Badge "Oferta Limitada" visible desde el inicio
 *   - Precio con descuento mostrado directamente
 *   - Sin modal de recuperación
 *   - Descuento aplicado automáticamente
 *   - Perfecto para Black Friday, Cyber Monday, etc.
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSiteSettingsVariant } from "@/hooks/useSiteSettingsVariant";
import { useInternationalHomeVipCheckoutPricing } from "@/hooks/useInternationalHomeVipCheckoutPricing";
import { lazy, Suspense } from "react";

const MercadoPagoSubscriptionBrick = lazy(
  () => import("@/components/MercadoPagoSubscriptionBrick").then((m) => ({ default: m.MercadoPagoSubscriptionBrick }))
);

/** Preload MercadoPago SDK on hover para checkout más rápido (solo ARS/MercadoPago) */
function preloadMercadoPagoOnHover(provider: string) {
  if (provider === "mercadopago") {
    void import("@/lib/mercadopagoPreload").then((m) => m.preloadMercadoPago());
  }
}
import {
  getLabelsForLanguage,
  getPremiumPlansFunnelSnapshot,
  resolveOfferEndsInLabel,
  type BioTemplatePremiumLabels,
} from "@/config/bioTemplatePremiumPlans";
import { initConversionTracking } from "@/lib/conversionAnalytics";
import {
  trackVipVideoOpenedPostHog,
  trackVipVideoClosedPostHog,
  trackDiscountOfferShownPostHog,
  trackDiscountOfferActionPostHog,
  trackPlanButtonClickPostHog,
  trackCheckoutPanelOpenedPostHog,
  trackCheckoutPanelClosedPostHog,
  trackEmailInputInteractionPostHog,
  trackEmailValidationErrorPostHog,
  trackCheckoutAbandonmentPostHog,
  identifyUserByEmailPostHog,
  setUserPropertiesPostHog,
  subscribePostHogClientReady,
  getPostHogCorrelationIdsPostHog,
  registerBioPremiumFunnelContextPostHog,
} from "@/lib/posthogAnalytics";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import {
  CheckCircle,
  Crown,
  ExternalLink,
  Gift,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUserId } from "@/hooks/useUserId";
import * as Sentry from "@sentry/react";
import { getDatafastAttributionPayload } from "@/lib/datafast";

// ─── Tipos públicos ───────────────────────────────────────────────────────────

export interface BioTemplatePremiumPlan {
  /** Precio en la moneda configurada (e.g. 29999 ARS, 29.99 USD) */
  price: number;
  /** Nombre del producto que se envía al checkout */
  productName: string;
  /** Texto breve debajo del precio en la card (e.g. "Pago único") */
  subtitle?: string;
}

export interface BioTemplatePremiumVipPlan extends BioTemplatePremiumPlan {
  /**
   * Mux playback ID para el video VIP. Si se pasa, al hacer click en "Unirme VIP"
   * se abre el video modal con un CTA que lleva al checkout o a exclusivePath.
   */
  playbackId?: string;
  /**
   * Ruta interna del CTA del video VIP (e.g. "/exclusive-ar?tier=vip").
   * Si no se pasa, el CTA del video abre el checkout embebido.
   */
  exclusivePath?: string;
}

export interface BioTemplatePremiumProps {
  // ── Planes ──────────────────────────────────────────────────────────────────
  /** Configuración del plan Standard (obligatorio) */
  standardPlan: BioTemplatePremiumPlan;
  /** Configuración del plan VIP (opcional — si no se pasa, no se muestra la card VIP) */
  vipPlan?: BioTemplatePremiumVipPlan;
  /**
   * Moneda del checkout. Determina el gateway de pago automáticamente:
   *   "ARS" → Mercado Pago Subscription (preapproval mensual)
   *   "USD" → Stripe Checkout Subscription (mensual)
   */
  currency: "ARS" | "USD";

  // ── Checkout ────────────────────────────────────────────────────────────────
  /**
   * Código de país ISO usado en el metadata del gateway (e.g. "AR", "US", "MX").
   * Por defecto: "AR"
   */
  country?: string;
  /**
   * Ruta relativa de éxito post-pago (e.g. "/ar/invitacion/success").
   * - MercadoPago: se usa como back_url del preapproval.
   * - Stripe: el backend agrega session_id al success_url para validar el pago.
   * Por defecto: "/ar/invitacion/success"
   */
  successPath?: string;
  /** Reservado para compatibilidad futura. */
  cancelPath?: string;


  // ── Branding ────────────────────────────────────────────────────────────────
  /** Nombre que se muestra debajo del logo. Por defecto: BRAND_PERSON_NAME */
  profileName?: string;

  // ── Idioma ──────────────────────────────────────────────────────────────────
  /**
   * Código de idioma base (e.g. "es", "en", "de", "fr").
   * Controla todos los textos de la UI. Soporta locales completos (e.g. "es-AR").
   * Por defecto: "es"
   */
  language?: string;

  // ── Identificador de flujo ──────────────────────────────────────────────────
  /**
   * Nombre del flujo para metadata y logs (e.g. "premium_invite", "us_premium").
   * Por defecto: "bio_template_premium"
   */
  flowName?: string;

  // ── WhatsApp Broadcast (opcional) ───────────────────────────────────────────
  /**
   * Configuración del canal de difusión de WhatsApp (opcional).
   * Si se pasa, se muestra un CTA sutil al final de la página.
   * Gateway según país: AR → MercadoPago (ARS), otros → Stripe (USD).
   */
  /**
   * Enlace al acceso gratuito (p. ej. Telegram). Por defecto el canal oficial.
   * Pasá `""` para no mostrar la card.
   */
  freeTelegramUrl?: string;

  whatsappBroadcast?: {
    /** Precio mensual en ARS para Argentina (e.g. 6999) */
    priceARS: number;
    /** Precio mensual en USD para otros países (e.g. 9.99) */
    priceUSD: number;
    /** Países donde se muestra (códigos ISO, e.g. ["AR", "MX", "US"]) */
    enabledCountries: string[];
    /** URL de éxito después del pago (e.g. "https://fan site/r/igcanal") */
    successUrl: string;
    /** Texto del CTA (opcional, por defecto: "Únete al Canal de WhatsApp") */
    ctaText?: string;
    /** Descripción breve (opcional) */
    description?: string;
  };
}

// ─── Internos ─────────────────────────────────────────────────────────────────

type PlanTier = "standard" | "vip";
type PaymentProvider = "mercadopago" | "stripe";
type CheckoutState = "idle" | "creating" | "redirecting" | "error" | "brick";

/**
 * Stripe-only para membresías. MercadoPago quedó deshabilitado para flujos
 * recurrentes; el tipo `PaymentProvider` y las ramas `mercadopago` se conservan
 * por compatibilidad de tipos / dead code futuro a limpiar.
 */
function deriveProvider(_currency: "ARS" | "USD"): PaymentProvider {
  return "stripe";
}

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

const DEFAULT_SUCCESS_PATH = "/ar/invitacion/success";

/** Canal público Telegram (sin pago). Cadena vacía oculta la card. */
const DEFAULT_FREE_TELEGRAM_URL = BRAND_TELEGRAM_URL;

// Descuento VIP cuando el usuario cierra el video (configurable via env var)
const VIP_DISCOUNT_RATE = parseFloat(
  (import.meta.env.VITE_PREMIUM_VIP_DISCOUNT_RATE as string) || "0.10"
);

// Descuento adicional por suscripción trimestral (3 meses) - solo para Stripe/USD
const QUARTERLY_DISCOUNT_RATE = parseFloat(
  (import.meta.env.VITE_PREMIUM_QUARTERLY_DISCOUNT_RATE as string) || "0.15"
);

function readSpecialOfferHours(): number {
  const raw = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER_HOURS as string | undefined)?.trim();
  const n = raw ? parseFloat(raw) : NaN;
  if (Number.isFinite(n) && n > 0) return Math.min(n, 168);
  return 15;
}

// Timestamp inyectado en build (vite define + .env.production.local) o 0 → fallback sessionStorage en cliente
const RAW_SPECIAL_OFFER_START = String(
  import.meta.env.VITE_PREMIUM_SPECIAL_OFFER_START ?? "",
).trim();
const SPECIAL_OFFER_START_FROM_BUILD = /^\d{10,}$/.test(RAW_SPECIAL_OFFER_START)
  ? parseInt(RAW_SPECIAL_OFFER_START, 10)
  : 0;

const SO_ANCHOR_SESSION_KEY = "bio_premium_special_offer_start_ms";

function resolveSpecialOfferAnchorMs(): number {
  if (SPECIAL_OFFER_START_FROM_BUILD > 0) return SPECIAL_OFFER_START_FROM_BUILD;
  if (typeof window === "undefined") return 0;
  try {
    const existing = sessionStorage.getItem(SO_ANCHOR_SESSION_KEY);
    const parsed = existing ? parseInt(existing, 10) : NaN;
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    const now = Date.now();
    sessionStorage.setItem(SO_ANCHOR_SESSION_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

/** Ventana del countdown desde START; horas vía VITE_PREMIUM_SPECIAL_OFFER_HOURS (default 15). */
const SPECIAL_OFFER_WINDOW_MS = readSpecialOfferHours() * 60 * 60 * 1000;

// Tipo para duración de suscripción (solo Stripe)
type BillingInterval = "monthly" | "quarterly";

// ─── Componente ───────────────────────────────────────────────────────────────

export default function BioTemplatePremium({
  standardPlan,
  vipPlan: vipPlanProp,
  currency,
  country = "AR",
  successPath = DEFAULT_SUCCESS_PATH,
  cancelPath,
  language = "es",
  profileName = BRAND_PERSON_NAME,
  flowName = "bio_template_premium",
  whatsappBroadcast,
  freeTelegramUrl = DEFAULT_FREE_TELEGRAM_URL,
}: BioTemplatePremiumProps) {
  const provider = deriveProvider(currency);
  const L: BioTemplatePremiumLabels = getLabelsForLanguage(language);
  const navigate = useNavigate();
  const userId = useUserId();

  // Admin VIP (country-templates) — same source as DualCTA for monthly VIP.
  const siteSettingsVariant = useSiteSettingsVariant();
  const { settings: siteSettings } = useSiteSettings(siteSettingsVariant);
  const { pricing: adminVipPricing } = useInternationalHomeVipCheckoutPricing({
    country,
    vipPricingByCountry: siteSettings.internationalHomeVipPricingByCountry,
    vipPricingDefault: siteSettings.internationalHomeVipPricingDefault,
    language,
  });
  /** VIP list price from admin; playback/subtitle still from prop/env. */
  const vipPlan = useMemo(() => {
    if (!vipPlanProp) return vipPlanProp;
    if (currency !== "USD") return vipPlanProp;
    return {
      ...vipPlanProp,
      price: adminVipPricing.fullPriceUsd,
    };
  }, [vipPlanProp, currency, adminVipPricing.fullPriceUsd]);
  
  // Determinar si hay promoción especial activa (tipo Black Friday)
  // Lee VITE_PREMIUM_SPECIAL_OFFER env var:
  // - "true" o "1" o "active" → Modo immediate (descuento visible desde inicio)
  // - cualquier otro valor o undefined → Modo recovery_modal (modal al cerrar video)
  const specialOfferEnv = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER as string)?.toLowerCase().trim();
  const isSpecialOfferActive = specialOfferEnv === "true" || specialOfferEnv === "1" || specialOfferEnv === "active";
  const isImmediateDiscount = isSpecialOfferActive;

  // Toggle maestro del "Elemento free-telegram" (admin `premium_invite_config`). false = oculto.
  const [freeTelegramEnabled, setFreeTelegramEnabled] = useState(true);
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (cfg) setFreeTelegramEnabled(cfg.free_telegram_enabled !== false);
      })
      .catch(() => {});
  }, []);

  /** Con Special Offer, si no hay URL explícita igual mostramos el Telegram por defecto. */
  // Sin free-telegram si el toggle maestro está OFF o en Argentina (AR va directo a Stripe):
  // oculta la card free y deja la Standard.
  const resolvedFreeTelegramUrl =
    !freeTelegramEnabled || (country ?? "").trim().toUpperCase().slice(0, 2) === "AR"
      ? ""
      : freeTelegramUrl.trim() || (isImmediateDiscount ? DEFAULT_FREE_TELEGRAM_URL : "");
  
  // Debug: log discount mode
  console.log("[BioTemplatePremium] Special Offer:", isSpecialOfferActive ? "ACTIVE 🔥" : "inactive", "| Env:", specialOfferEnv || "undefined");
  
  // Per-mount session id — groups all events from one visit even for anon users
  const premiumSessionIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `bp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );

  // Debug: log currency and provider
  console.log("[BioTemplatePremium] Currency:", currency, "→ Provider:", provider, "| Country:", country);

  // ── Estado ────────────────────────────────────────────────────────────────
  const [showVipVideo, setShowVipVideo] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [vipVideoWatched, setVipVideoWatched] = useState(false);
  const [checkoutState, setCheckoutState] = useState<CheckoutState>("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showCustomCheckout, setShowCustomCheckout] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<PlanTier>("standard");
  // Billing interval: monthly (default) or quarterly (3 months with 15% extra discount) - solo para Stripe/USD
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  /** Params for MercadoPago Bricks subscription flow (embeddded checkout) */
  const [brickCheckoutParams, setBrickCheckoutParams] = useState<{
    plan: PlanTier;
    finalPrice: number;
    packName: string;
    successPath: string;
  } | null>(null);
  
  // ── WhatsApp Broadcast state ──────────────────────────────────────────────
  // País habilitado o Special Offer (mostrar también en campaña global)
  const showWhatsappBroadcast =
    whatsappBroadcast != null &&
    (whatsappBroadcast.enabledCountries.includes(country) || isImmediateDiscount);
  
  console.log("[BioTemplatePremium] WhatsApp Broadcast:", {
    whatsappBroadcast: !!whatsappBroadcast,
    country,
    enabledCountries: whatsappBroadcast?.enabledCountries,
    showWhatsappBroadcast,
  });
  
  // ── Countdown GLOBAL Special Offer (ancla en build o sessionStorage en dev) ──
  const specialOfferAnchorRef = useRef(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    if (!isImmediateDiscount) return 0;
    const anchor = resolveSpecialOfferAnchorMs();
    specialOfferAnchorRef.current = anchor;
    if (!anchor || anchor <= 0) return 0;
    const remaining = Math.max(0, SPECIAL_OFFER_WINDOW_MS - (Date.now() - anchor));
    if (SPECIAL_OFFER_START_FROM_BUILD <= 0 && anchor > 0) {
      console.warn(
        "[BioTemplatePremium] Special Offer: sin VITE_PREMIUM_SPECIAL_OFFER_START en build; countdown anclado a esta sesión (ver deploy / .env.production.local).",
      );
    }
    console.log("[BioTemplatePremium] Countdown global:", {
      anchorFromBuild: SPECIAL_OFFER_START_FROM_BUILD > 0,
      startTimestamp: anchor,
      startDate: new Date(anchor).toISOString(),
      remainingMin: Math.round(remaining / 1000 / 60),
    });
    return remaining;
  });

  // Inicializar tracking de conversiones al cargar la página
  useEffect(() => {
    initConversionTracking();
  }, []);

  // Server-side view tracking → /admin/checkout-funnel (best-effort, no auth)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const urlParams = new URLSearchParams(window.location.search);
    const phDistinctId = (window as unknown as { posthog?: { get_distinct_id?: () => string } })
      .posthog?.get_distinct_id?.();
    void fetch("/api/track-template-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        template: "BioTemplatePremium",
        template_variant: currency === "ARS" ? "ar_mp_brick" : "intl_stripe",
        flow_name: flowName,
        landing_path: window.location.pathname,
        country,
        language,
        posthog_distinct_id: phDistinctId,
        customer_email: urlParams.get("email") ?? undefined,
        utm_source: urlParams.get("utm_source") ?? undefined,
        utm_medium: urlParams.get("utm_medium") ?? undefined,
        utm_campaign: urlParams.get("utm_campaign") ?? undefined,
      }),
    }).catch(() => {/* fire-and-forget */});
    // Run once per (country, currency, flow) — those are stable for a given mount
  }, [country, currency, language, flowName]);

  useEffect(() => {
    if (!isImmediateDiscount) return;
    const anchor = specialOfferAnchorRef.current;
    if (!anchor || anchor <= 0) return;
    const tick = () => {
      setTimeRemaining(Math.max(0, SPECIAL_OFFER_WINDOW_MS - (Date.now() - anchor)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isImmediateDiscount]);
  
  // ── Email del checkout (obligatorio para receipts y abandoned checkout recovery) ──
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const checkoutInFlightRef = useRef(false);
  const checkoutPanelOpenTimeRef = useRef<number | null>(null);
  const checkoutOpenSourceRef = useRef<"direct_button" | "video_cta" | "discount_accepted">("direct_button");
  const [flowTraceId] = useState(() => {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
    } catch {
      // fallback below
    }
    return `flow_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  });

  useEffect(() => {
    return subscribePostHogClientReady(() => {
      registerBioPremiumFunnelContextPostHog({
        bio_premium_flow_trace_id: flowTraceId,
        bio_premium_tab_session_id: premiumSessionIdRef.current,
        template_funnel: flowName,
      });
    });
  }, [flowTraceId, flowName]);

  const capturePremiumEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const funnelSnap = getPremiumPlansFunnelSnapshot({ currency, language });
    const corr = getPostHogCorrelationIdsPostHog();
    const mergedProps: Record<string, unknown> = {
      ...funnelSnap,
      flow_trace_id: flowTraceId,
      bio_premium_tab_session_id: premiumSessionIdRef.current,
      premium_visit_flow_trace_id: flowTraceId,
      ...corr,
      ...props,
    };
    const posthogWin = (
      window as unknown as {
        posthog?: {
          get_distinct_id?: () => string;
        };
      }
    ).posthog;
    mirrorFunnelEvent({
      event_name: event,
      posthog_distinct_id: posthogWin?.get_distinct_id?.(),
      session_id: premiumSessionIdRef.current,
      customer_email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkoutEmail.trim())
        ? checkoutEmail.trim()
        : undefined,
      template: "BioTemplatePremium",
      template_variant: currency === "ARS" ? "ar_mp_brick" : "intl_stripe",
      flow_name: flowName,
      landing_path: window.location.pathname,
      country,
      language,
      page_url: window.location.href,
      props: mergedProps,
    });
  };
  
  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError(L.emailRequired);
      trackEmailValidationErrorPostHog("empty", checkoutPlan, flowName, country);
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      setEmailError(L.emailInvalid);
      trackEmailValidationErrorPostHog("invalid", checkoutPlan, flowName, country);
      return false;
    }
    setEmailError(null);
    return true;
  };

  const emailRequiredMessage = L.emailRequiredCheckout;
  
  // Formatear tiempo restante del countdown
  const formatTimeRemaining = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // ── Discount tracking (solo para modo recovery_modal) ────────────────────────
  const DISCOUNT_OFFER_SHOWN_KEY = `bio_premium_discount_shown_${flowName}`;
  const DISCOUNT_ACCEPTED_KEY = `bio_premium_discount_accepted_${flowName}`;
  const DISCOUNT_ACCEPTED_TIMESTAMP_KEY = `bio_premium_discount_timestamp_${flowName}`;
  
  // Tiempo de expiración del descuento: 24 horas (en milisegundos)
  const DISCOUNT_EXPIRATION_MS = 24 * 60 * 60 * 1000;

  const [discountOfferShown, setDiscountOfferShown] = useState(() => {
    if (isImmediateDiscount) return false; // No aplica en modo immediate
    try {
      return localStorage.getItem(DISCOUNT_OFFER_SHOWN_KEY) === "true";
    } catch {
      return false;
    }
  });
  
  const [discountAccepted, setDiscountAccepted] = useState(() => {
    if (isImmediateDiscount) return true; // Siempre activo en modo immediate
    try {
      const accepted = localStorage.getItem(DISCOUNT_ACCEPTED_KEY) === "true";
      if (!accepted) return false;
      
      // Verificar si el descuento ha expirado
      const timestampStr = localStorage.getItem(DISCOUNT_ACCEPTED_TIMESTAMP_KEY);
      if (!timestampStr) {
        // Si no hay timestamp, limpiar el descuento (legacy data)
        localStorage.removeItem(DISCOUNT_ACCEPTED_KEY);
        return false;
      }
      
      const timestamp = parseInt(timestampStr, 10);
      const now = Date.now();
      const elapsed = now - timestamp;
      
      if (elapsed > DISCOUNT_EXPIRATION_MS) {
        // Descuento expirado - limpiar localStorage
        console.log("[BioTemplatePremium] Discount expired, clearing localStorage");
        localStorage.removeItem(DISCOUNT_ACCEPTED_KEY);
        localStorage.removeItem(DISCOUNT_ACCEPTED_TIMESTAMP_KEY);
        return false;
      }
      
      console.log("[BioTemplatePremium] Discount still valid:", {
        elapsed: Math.round(elapsed / 1000 / 60), // minutos
        remaining: Math.round((DISCOUNT_EXPIRATION_MS - elapsed) / 1000 / 60), // minutos
      });
      
      return true;
    } catch {
      return false;
    }
  });

  
  // Elegibilidad: todos los usuarios son elegibles (API deshabilitada)
  // El estado de "ya visto" se gestiona solo con localStorage.

  // Track discount offer in database (solo para modo recovery_modal)
  const trackDiscountOffer = async (action: "accepted" | "declined", email?: string) => {
    if (!vipPlan || isImmediateDiscount) return;
    
    try {
      await fetch("/api/track-discount-offer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flowName,
          action,
          planTier: "vip",
          originalPrice: vipPlan.price,
          discountedPrice: Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
          currency,
          country,
          userAgent: navigator.userAgent,
          userId,
          discountRate: VIP_DISCOUNT_RATE,
          buyerEmail: email || checkoutEmail.trim() || undefined,
        }),
      });
    } catch (error) {
      console.error("[BioTemplatePremium] Error tracking discount:", error);
      Sentry.captureException(error, {
        tags: {
          flow: flowName,
          component: "BioTemplatePremium",
          operation: "track_discount_offer",
        },
        contexts: {
          discount: {
            action,
            email,
            flowName,
            userId,
          },
        },
      });
    }
  };
  
  const effectiveCancelPath = cancelPath || "/invitacion-premium";

  // ── Body scroll lock ──────────────────────────────────────────────────────
  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    const origHeight = document.body.style.height;
    const origPosition = document.body.style.position;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    return () => {
      document.body.style.overflow = origOverflow;
      document.body.style.height = origHeight;
      document.body.style.position = origPosition;
    };
  }, []);

  // ── Tier visual (basado en el plan seleccionado y modo Special Offer) ───────
  const tierConfig = useMemo(() => {
    // En Special Offer mode, usar colores verdes para todo (coherencia y urgencia)
    if (isImmediateDiscount) {
      return { 
        gradient: "from-green-500 via-emerald-500 to-teal-500", 
        badge: "SPECIAL OFFER", 
        icon: Sparkles 
      };
    }
    
    // Modo normal: colores según plan seleccionado
    if (checkoutPlan === "vip") {
      return { gradient: "from-purple-500 via-pink-500 to-rose-500", badge: "VIP", icon: Sparkles };
    }
    return { gradient: "from-blue-500 via-indigo-500 to-purple-500", badge: "EXCLUSIVO", icon: Gift };
  }, [checkoutPlan, isImmediateDiscount]);

  const TierIcon = tierConfig.icon;

  // ── Layout ────────────────────────────────────────────────────────────────
  // Background desde env var (Railway): VITE_PREMIUM_BACKGROUND_IMAGE_URL
  // En prod debe estar configurada en Railway; se valida para evitar url('') o URLs rotas
  const rawBackgroundUrl = (import.meta.env.VITE_PREMIUM_BACKGROUND_IMAGE_URL as string | undefined)?.trim();
  const resolvedBackgroundUrl =
    rawBackgroundUrl && rawBackgroundUrl.startsWith("http") ? rawBackgroundUrl : undefined;
  
  // GIF de fondo para checkout Standard (diferencial visual vs VIP que tiene Mux video)
  const rawStandardCheckoutBgGif = (import.meta.env.VITE_STANDARD_CHECKOUT_BG_GIF as string | undefined)?.trim();
  const standardCheckoutBgGif =
    rawStandardCheckoutBgGif && rawStandardCheckoutBgGif.startsWith("http") ? rawStandardCheckoutBgGif : undefined;
  const ctaText = L.ctaDefault;

  // ── Formateo de precio ────────────────────────────────────────────────────
  const priceLocale = currency === "USD" ? "en-US" : "es-AR";
  const formatPrice = (value: number) =>
    new Intl.NumberFormat(priceLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: currency === "USD" ? 2 : 0,
      maximumFractionDigits: currency === "USD" ? 2 : 0,
    }).format(value);

  // ── Labels del gateway ────────────────────────────────────────────────────
  const gatewayLabel = provider === "mercadopago" ? "Mercado Pago" : "Stripe";
  const checkoutTypeLabel = L.footerSubscription;  // "Suscripción mensual"
  const checkoutDescLabel = L.checkoutDescSubscription;  // "Suscripción mensual. Podés cancelar cuando quieras."

  /** GIF en capa full-screen (no solo la altura del formulario — evita banda vacía en móvil). */
  const fullBleedStandardCheckoutGif =
    Boolean(standardCheckoutBgGif) &&
    showCustomCheckout &&
    checkoutOpenSourceRef.current !== "video_cta" &&
    ((isImmediateDiscount && checkoutPlan === "standard") || provider === "mercadopago");

  /** Formulario email + resumen antes del Payment Brick (MP): pantalla opaca y sin GIF de fondo para no “ver” la landing. */
  const mercadoPagoFormCheckoutOpen =
    showCustomCheckout && provider === "mercadopago" && checkoutState !== "brick";

  const checkoutMediaFullBleed = fullBleedStandardCheckoutGif && !mercadoPagoFormCheckoutOpen;

  // ── Plan activo en el checkout panel ──────────────────────────────────────
  const activePlan = checkoutPlan === "vip" && vipPlan ? vipPlan : standardPlan;

  // ── Checkout ──────────────────────────────────────────────────────────────
  const handleCheckout = async (planOverride?: PlanTier) => {
    if (checkoutInFlightRef.current || checkoutState === "creating" || checkoutState === "redirecting" || checkoutState === "brick") {
      return;
    }

    const plan = planOverride ?? checkoutPlan;
    const planConfig = plan === "vip" && vipPlan ? vipPlan : standardPlan;

    // Validar email antes de proceder (obligatorio para receipts y recovery)
    setEmailTouched(true);
    if (!validateEmail(checkoutEmail)) {
      capturePremiumEvent("premium_checkout_blocked_email", {
        flow: flowName,
        flow_trace_id: flowTraceId,
        provider,
        country,
        currency,
        plan_tier: plan,
      });
      // Important for VIP-video direct CTA path: if email is missing,
      // force open checkout panel so user can input it and continue.
      setShowCustomCheckout(true);
      setCheckoutError(emailRequiredMessage);
      return;
    }

    // Synchronous lock to prevent click/touch races creating duplicate subscriptions.
    checkoutInFlightRef.current = true;

    setCheckoutState("creating");
    setCheckoutError(null);

    const encodedPlan = encodeURIComponent(plan);
    const encodedTraceId = encodeURIComponent(flowTraceId);
    const withPlan = successPath.includes("?")
      ? `${successPath}&plan=${encodedPlan}`
      : `${successPath}?plan=${encodedPlan}`;
    const withTraceId = withPlan.includes("flow_trace_id=")
      ? withPlan
      : `${withPlan}&flow_trace_id=${encodedTraceId}`;
    const planSuccessPath = withTraceId.includes("provider=")
      ? withTraceId
      : `${withTraceId}&provider=${provider}`;

    // Aplicar descuento según el modo:
    // - immediate: aplicado a AMBOS planes (standard y vip) cuando special offer está activo
    // - recovery_modal: aplicado a AMBOS planes (standard y vip) si el usuario aceptó explícitamente
    const shouldApplyDiscount = discountAccepted;
    
    // Calcular descuento base (Special Offer)
    const baseDiscountRate = shouldApplyDiscount ? VIP_DISCOUNT_RATE : 0;
    
    // Descuento adicional por trimestre (solo Stripe/USD y billing quarterly)
    const isQuarterly = provider === "stripe" && billingInterval === "quarterly";
    const quarterlyDiscountRate = isQuarterly ? QUARTERLY_DISCOUNT_RATE : 0;
    
    // Descuento total combinado: (1 - base) * (1 - quarterly) = precio final
    // Ejemplo: 10% base + 15% quarterly = 1 - (0.9 * 0.85) = 23.5% total
    const combinedMultiplier = (1 - baseDiscountRate) * (1 - quarterlyDiscountRate);
    const totalDiscountRate = 1 - combinedMultiplier;
    
    // Precio unitario con descuentos aplicados
    const unitPriceWithDiscount = Math.round(planConfig.price * combinedMultiplier);
    
    // Para trimestral: precio total = precio mensual * 3
    const intervalCount = isQuarterly ? 3 : 1;
    const finalPrice = unitPriceWithDiscount * intervalCount;
    
    console.log("[BioTemplatePremium] Price calculation:", {
      billingInterval,
      intervalCount,
      originalPrice: planConfig.price,
      baseDiscountRate: `${Math.round(baseDiscountRate * 100)}%`,
      quarterlyDiscountRate: `${Math.round(quarterlyDiscountRate * 100)}%`,
      totalDiscountRate: `${Math.round(totalDiscountRate * 100)}%`,
      unitPriceWithDiscount,
      finalPrice,
    });
    
    // Agregar indicador de descuento al nombre del producto para que se vea en el gateway
    const discountLabel = isQuarterly
      ? `${Math.round(totalDiscountRate * 100)}% OFF - 3 meses`
      : shouldApplyDiscount
        ? `${Math.round(VIP_DISCOUNT_RATE * 100)}% OFF`
        : "";
    const productNameWithDiscount = discountLabel
      ? `${planConfig.productName} (${discountLabel})`
      : planConfig.productName;

    const buyerEmail = checkoutEmail.trim();
    identifyUserByEmailPostHog(buyerEmail, {
      plan_tier: plan,
      currency,
      country,
      flow_name: flowName,
      checkout_started_at: new Date().toISOString(),
    });
    setUserPropertiesPostHog({
      last_checkout_attempt: new Date().toISOString(),
      preferred_currency: currency,
      interested_plan: plan,
    });
    capturePremiumEvent("premium_checkout_start", {
      flow: flowName,
      flow_trace_id: flowTraceId,
      provider,
      country,
      currency,
      plan_tier: plan,
      price: finalPrice,
      discount_applied: shouldApplyDiscount,
    });

    try {
      if (provider === "mercadopago") {
        // ARS → MercadoPago Bricks (suscripción embebida, sin redirect)
        setBrickCheckoutParams({
          plan,
          finalPrice,
          packName: productNameWithDiscount,
          successPath: planSuccessPath,
        });
        setCheckoutState("brick");
        checkoutInFlightRef.current = false;
        return;
      }

      else {
        // USD → Stripe Checkout Session (mensual o trimestral)
        // VIP monthly: admin Price + promo (DualCTA path). Skip legacy PROMO{n}
        // so Stripe charges the real Price, not an inline amount.
        const useAdminVipCharge =
          plan === "vip" &&
          intervalCount === 1 &&
          Boolean(adminVipPricing.stripePriceId);

        const amountCents = useAdminVipCharge
          ? adminVipPricing.amountCents > 0
            ? adminVipPricing.amountCents
            : Math.round(finalPrice * 100)
          : Math.round(finalPrice * 100);
        const successUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${planSuccessPath}`
            : planSuccessPath;
        const cancelUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${effectiveCancelPath}`
            : effectiveCancelPath;

        // Precio original para mostrar tachado (sin descuentos, multiplicado por intervalCount)
        const originalPriceTotal = planConfig.price * intervalCount;
        const hasAnyDiscount = useAdminVipCharge
          ? false
          : shouldApplyDiscount || isQuarterly;

        const response = await fetch("/api/create-subscription-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...getDatafastAttributionPayload(),
            amount: amountCents,
            currency: "usd",
            productName: useAdminVipCharge
              ? planConfig.productName
              : productNameWithDiscount,
            buyerEmail,
            country,
            successUrl,
            cancelUrl,
            intervalCount,
            ...(useAdminVipCharge
              ? {
                  priceId: adminVipPricing.stripePriceId,
                  ...(adminVipPricing.promotionCodeId
                    ? { promotionCodeId: adminVipPricing.promotionCodeId }
                    : {}),
                }
              : {
                  originalAmount: hasAnyDiscount
                    ? Math.round(originalPriceTotal * 100)
                    : undefined,
                  discountPercentage: hasAnyDiscount
                    ? Math.round(totalDiscountRate * 100)
                    : undefined,
                }),
            metadata: {
              source: BRAND_SOURCE_KEY,
              flow: flowName,
              flow_trace_id: flowTraceId,
              pack_id: `${flowName}_${plan}`,
              plan_tier: plan,
              ...(plan === "vip" ? { product_type: "exclusive" } : {}),
              product_name: planConfig.productName,
              original_price: planConfig.price,
              billing_interval: billingInterval,
              interval_count: String(intervalCount),
              discount_applied: useAdminVipCharge
                ? adminVipPricing.discountPct > 0
                  ? `${adminVipPricing.discountPct}%_admin_promo`
                  : "none"
                : hasAnyDiscount
                  ? `${Math.round(totalDiscountRate * 100)}%`
                  : "none",
              quarterly_discount: isQuarterly
                ? `${Math.round(QUARTERLY_DISCOUNT_RATE * 100)}%`
                : "none",
              buyer_email: buyerEmail,
              template: "BioTemplatePremium",
              template_variant: `${plan}_${billingInterval}`,
              price_source: useAdminVipCharge
                ? adminVipPricing.priceSource
                : "bio_plan_config",
              checkout_started_at: new Date().toISOString(),
              ...(useAdminVipCharge && adminVipPricing.stripePriceId
                ? { stripe_price_id: adminVipPricing.stripePriceId }
                : {}),
            },
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.url) {
          capturePremiumEvent("premium_checkout_api_error", {
            flow: flowName,
            flow_trace_id: flowTraceId,
            provider,
            country,
            currency,
            plan_tier: plan,
            status: response.status,
            error: data.error || "unknown",
            code: data.code || "unknown",
          });
          
          // Capturar error en Sentry
          Sentry.captureMessage("Stripe checkout API error", {
            level: "error",
            tags: {
              flow: flowName,
              component: "BioTemplatePremium",
              operation: "stripe_checkout_api_error",
              provider: "stripe",
              plan_tier: plan,
              country,
              currency,
              error_code: data.code || "unknown",
            },
            contexts: {
              checkout: {
                flowTraceId,
                buyerEmail,
                planTier: plan,
                price: finalPrice,
                status: response.status,
                error: data.error,
                code: data.code,
              },
            },
            user: {
              email: buyerEmail,
              id: userId,
            },
          });
          
          // Notificar a admins (error crítico de Stripe)
          void fetch("/api/notify-checkout-failure", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              flowName,
              flowTraceId,
              buyerEmail,
              planTier: plan,
              provider: "stripe",
              country,
              currency,
              price: finalPrice,
              error: data.error || "Stripe API error",
              timestamp: new Date().toISOString(),
            }),
          }).catch((notifyErr) => {
            console.error("[BioTemplatePremium] Failed to notify admins:", notifyErr);
          });
          
          // Enviar email de recuperación al usuario (la API genera un nuevo link de checkout)
          void fetch("/api/recover-checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            ...getDatafastAttributionPayload(),
              buyerEmail,
              buyerName: buyerEmail.split("@")[0],
              planTier: plan,
              provider: "stripe",
              country,
              currency,
              price: finalPrice,
              originalPrice: planConfig.price * intervalCount,
              discountPercentage: totalDiscountRate > 0 ? Math.round(totalDiscountRate * 100) : undefined,
              intervalCount,
              flowTraceId,
              flowName,
            }),
          }).catch((recoverErr) => {
            console.error("[BioTemplatePremium] Failed to send recovery email:", recoverErr);
          });
          
          const timeInCheckoutStripeErr = checkoutPanelOpenTimeRef.current
            ? Math.round((Date.now() - checkoutPanelOpenTimeRef.current) / 1000)
            : 0;
          trackCheckoutPanelClosedPostHog(plan, true, timeInCheckoutStripeErr, "error", flowName, country);
          setCheckoutState("error");
          setCheckoutError(data.error || "Error al crear suscripción en Stripe");
          checkoutInFlightRef.current = false;
          return;
        }

        setCheckoutState("redirecting");
        const timeInCheckoutStripe = checkoutPanelOpenTimeRef.current
          ? Math.round((Date.now() - checkoutPanelOpenTimeRef.current) / 1000)
          : 0;
        trackCheckoutPanelClosedPostHog(plan, true, timeInCheckoutStripe, "completed", flowName, country);
        capturePremiumEvent("premium_checkout_redirect", {
          flow: flowName,
          flow_trace_id: flowTraceId,
          provider,
          country,
          currency,
          plan_tier: plan,
          redirect_to: "stripe_checkout_url",
        });
        try {
          const sessionId =
            typeof data.sessionId === "string" ? data.sessionId.trim() : "";
          if (sessionId) {
            localStorage.setItem(
              "premium_last_stripe_session",
              JSON.stringify({
                sessionId,
                flowTraceId,
                flowName,
                plan,
                createdAt: Date.now(),
              }),
            );
          }
        } catch {
          // Non-blocking fallback storage for success-page recovery.
        }
        
        // Validar URL antes de redirigir
        const checkoutUrl = data.url as string;
        if (!checkoutUrl || !checkoutUrl.startsWith("https://checkout.stripe.com/")) {
          console.error("[BioTemplatePremium] Invalid Stripe checkout URL:", checkoutUrl);
          capturePremiumEvent("premium_checkout_invalid_url", {
            flow: flowName,
            flow_trace_id: flowTraceId,
            provider: "stripe",
            country,
            currency,
            plan_tier: plan,
            url: checkoutUrl,
          });
          
          // Enviar email de recuperación al usuario (la API genera un nuevo link válido)
          void fetch("/api/recover-checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            ...getDatafastAttributionPayload(),
              buyerEmail,
              buyerName: buyerEmail.split("@")[0],
              planTier: plan,
              provider: "stripe",
              country,
              currency,
              price: finalPrice,
              originalPrice: planConfig.price * intervalCount,
              discountPercentage: totalDiscountRate > 0 ? Math.round(totalDiscountRate * 100) : undefined,
              intervalCount,
              flowTraceId,
              flowName,
            }),
          }).catch((recoverErr) => {
            console.error("[BioTemplatePremium] Failed to send recovery email:", recoverErr);
          });
          
        setCheckoutState("error");
        setCheckoutError(L.errorLinkGenerated);
          checkoutInFlightRef.current = false;
          return;
        }
        
        // Intentar redirigir con manejo de errores y timeout
        let redirectCompleted = false;
        
        // Timeout de 3 segundos para detectar si el redirect está bloqueado
        const redirectTimeout = setTimeout(() => {
          if (!redirectCompleted) {
            console.warn("[BioTemplatePremium] Redirect timeout - possible blocker");
            capturePremiumEvent("premium_checkout_redirect_timeout", {
              flow: flowName,
              flow_trace_id: flowTraceId,
              provider: "stripe",
              country,
              currency,
              plan_tier: plan,
            });
            
            // Enviar email de recuperación con el link
            void fetch("/api/send-checkout-recovery-email", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                buyerEmail,
                buyerName: buyerEmail.split("@")[0], // Usar parte del email como nombre
                checkoutUrl,
                planName: planConfig.productName,
                price: finalPrice,
                currency: "USD",
                flowTraceId,
              }),
            }).catch((emailErr) => {
              console.error("[BioTemplatePremium] Failed to send recovery email:", emailErr);
            });
            
            // Mostrar mensaje con link manual
            const message =
              `${L.checkoutRedirectPart1} ` +
              `<a href="${checkoutUrl}" target="_blank" rel="noopener noreferrer" ` +
              `style="color: #a855f7; text-decoration: underline; font-weight: 600;">` +
              `${L.checkoutRedirectLinkText}</a><br><br>` +
              L.checkoutRedirectPart2;
            
            setCheckoutState("error");
            setCheckoutError(message);
            checkoutInFlightRef.current = false;
          }
        }, 3000);
        
        try {
          window.location.href = checkoutUrl;
          redirectCompleted = true;
          clearTimeout(redirectTimeout);
        } catch (redirectError) {
          clearTimeout(redirectTimeout);
          console.error("[BioTemplatePremium] Redirect failed:", redirectError);
          capturePremiumEvent("premium_checkout_redirect_failed", {
            flow: flowName,
            flow_trace_id: flowTraceId,
            provider: "stripe",
            country,
            currency,
            plan_tier: plan,
            error: redirectError instanceof Error ? redirectError.message : "Load failed",
          });
          
          // Fallback: abrir en nueva pestaña
          try {
            const newWindow = window.open(checkoutUrl, "_blank");
            if (!newWindow) {
              throw new Error("Popup blocked");
            }
            redirectCompleted = true;
          } catch (popupError) {
            // Enviar email de recuperación
            void fetch("/api/send-checkout-recovery-email", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                buyerEmail,
                buyerName: buyerEmail.split("@")[0],
                checkoutUrl,
                planName: planConfig.productName,
                price: finalPrice,
                currency: "USD",
                flowTraceId,
              }),
            }).catch((emailErr) => {
              console.error("[BioTemplatePremium] Failed to send recovery email:", emailErr);
            });
            
            const message =
              `${L.checkoutPopupPart1} ` +
              `<a href="${checkoutUrl}" target="_blank" rel="noopener noreferrer" ` +
              `style="color: #a855f7; text-decoration: underline; font-weight: 600;">` +
              `${L.checkoutRedirectLinkText}</a> ${L.checkoutPopupPart2}<br><br>` +
              L.checkoutRedirectPart2;
            
            setCheckoutState("error");
            setCheckoutError(message);
            checkoutInFlightRef.current = false;
          }
        }
      }
    } catch (error) {
      const timeInCheckoutCatch = checkoutPanelOpenTimeRef.current
        ? Math.round((Date.now() - checkoutPanelOpenTimeRef.current) / 1000)
        : 0;
      trackCheckoutPanelClosedPostHog(plan, true, timeInCheckoutCatch, "error", flowName, country);
      capturePremiumEvent("premium_checkout_network_error", {
        flow: flowName,
        flow_trace_id: flowTraceId,
        provider,
        country,
        currency,
        plan_tier: plan,
        error: error instanceof Error ? error.message : "unknown",
      });
      console.error(`[BioTemplatePremium:${flowName}] ${gatewayLabel} checkout error:`, error);
      
      // Capturar error crítico en Sentry y notificar a admins
      Sentry.captureException(error, {
        level: "error",
        tags: {
          flow: flowName,
          component: "BioTemplatePremium",
          operation: "checkout_failed",
          provider,
          plan_tier: plan,
          country,
          currency,
        },
        contexts: {
          checkout: {
            flowTraceId,
            buyerEmail: checkoutEmail,
            planTier: plan,
            price: finalPrice,
            discountApplied: shouldApplyDiscount,
            timeInCheckout: timeInCheckoutCatch,
            provider,
            country,
            currency,
          },
        },
        user: {
          email: checkoutEmail,
          id: userId,
        },
      });
      
      // Enviar notificación a admins via API (Resend)
      void fetch("/api/notify-checkout-failure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flowName,
          flowTraceId,
          buyerEmail: checkoutEmail,
          planTier: plan,
          provider,
          country,
          currency,
          price: finalPrice,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        }),
      }).catch((notifyErr) => {
        console.error("[BioTemplatePremium] Failed to notify admins:", notifyErr);
      });
      
      // Enviar email de recuperación al usuario para que pueda volver a intentarlo
      if (provider === "stripe" && checkoutEmail) {
        void fetch("/api/recover-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...getDatafastAttributionPayload(),
            buyerEmail: checkoutEmail,
            buyerName: checkoutEmail.split("@")[0],
            planTier: plan,
            provider: "stripe",
            country,
            currency,
            price: finalPrice,
            originalPrice: planConfig.price * intervalCount,
            discountPercentage: totalDiscountRate > 0 ? Math.round(totalDiscountRate * 100) : undefined,
            intervalCount,
            flowTraceId,
            flowName,
          }),
        }).catch((recoverErr) => {
          console.error("[BioTemplatePremium] Failed to send recovery email:", recoverErr);
        });
      }
      
      setCheckoutState("error");
      setCheckoutError(L.connectionError);
      checkoutInFlightRef.current = false;
    }
  };

  // ── WhatsApp Broadcast Checkout ───────────────────────────────────────────
  const handleWhatsappCheckout = () => {
    if (!whatsappBroadcast) return;

    // Track event
    capturePremiumEvent("whatsapp_broadcast_button_click", {
      flow: flowName,
      flow_trace_id: flowTraceId,
      country,
      source: "bio_template_premium",
      destination: "/whatsapp-broadcast",
      gateway: "internal_navigate",
    });

    // Redirigir a la página de WhatsApp Broadcast
    navigate("/whatsapp-broadcast");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 w-full h-full overflow-hidden text-white font-body lining-nums tabular-nums"
    >
      {/* ── Video VIP modal ──────────────────────────────────────────────────────── */}
      {showVipVideo && vipPlan?.playbackId && (
        <div className="fixed inset-0 z-50 bg-black w-full h-dvh min-h-dvh overflow-hidden">
          <MuxVideoPlayerWithCTA
            countryCode={country}
            language={language.split("-")[0] as "en" | "es" | "de" | "fr" | "it" | "pt" | "nl"}
            playbackId={vipPlan.playbackId}
            forceManualPlaybackId={true}
            ctaSubtitle={vipPlan.subtitle}
            ctaUrl={`${BRAND_SITE_URL}/checkout-vip`}
            ctaLabel={L.joinVip}
            hideUnmutePrompt={true}
            onCTAClick={async (url) => {
              setShowVipVideo(false);
              setCheckoutPlan("vip");
              checkoutOpenSourceRef.current = "video_cta";
              checkoutPanelOpenTimeRef.current = Date.now();
              if (vipPlan) {
                const price = (isImmediateDiscount || discountAccepted)
                  ? Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE))
                  : vipPlan.price;
                trackCheckoutPanelOpenedPostHog(
                  "vip",
                  "video_cta",
                  price,
                  currency,
                  flowName,
                  country,
                );
              }
              setCheckoutError(null);
              setShowCustomCheckout(true);
            }}
            onClose={() => {
              setShowVipVideo(false);
              setVipVideoWatched(true);
              if (vipPlan?.playbackId) {
                trackVipVideoClosedPostHog(flowName, vipPlan.playbackId, country);
              }
              
              // Mostrar modal de descuento solo en modo recovery_modal
              if (!isImmediateDiscount && !discountOfferShown && !showCustomCheckout) {
                setTimeout(() => {
                  setShowDiscountModal(true);
                  if (vipPlan) {
                    // Mirror al funnel para ver en admin + Slack
                    capturePremiumEvent("discount_offer_shown", {
                      plan_tier: "vip",
                      original_price: vipPlan.price,
                      final_price: Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                      discount_pct: Math.round(VIP_DISCOUNT_RATE * 100),
                      currency,
                      trigger: "video_closed",
                    });
                    trackDiscountOfferShownPostHog(
                      flowName,
                      "vip",
                      vipPlan.price,
                      Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                      currency,
                      country
                    );
                  }
                }, 500);
              }
            }}
          />
        </div>
      )}

      {/* ── Discount modal (recovery_modal mode only) ──────────────────────────── */}
      {!isImmediateDiscount && showDiscountModal && vipVideoWatched && vipPlan && !discountOfferShown && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center p-5 animate-fade-in">
          <div className="relative max-w-[360px] w-full">
            {/* Subtle glow effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 rounded-3xl blur-lg" />
            
            {/* Modal content */}
            <div className="relative bg-black border border-white/[0.12] rounded-3xl p-8 text-center space-y-5 shadow-2xl">
              {/* Gift icon */}
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" />
                <div className="relative text-6xl">🎁</div>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  {L.discountWait}
                </h3>
                <p className="text-white/70 text-base leading-relaxed px-2 font-medium tracking-tight">
                  {L.discountGiftPct.replace("{pct}", String(Math.round(VIP_DISCOUNT_RATE * 100)))}
                </p>
                <p className="text-white/50 text-sm tracking-tight">
                  {L.discountVipSub}
                </p>
              </div>

              {/* Price comparison */}
              <div className="bg-white/[0.04] backdrop-blur-sm rounded-2xl p-5 border border-white/[0.08] space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-[13px] font-medium tracking-tight">
                    {L.discountOriginalPrice}
                  </span>
                  <span className="text-white/50 text-[15px] line-through decoration-white/30 decoration-2 font-medium tracking-tight">
                    {formatPrice(vipPlan.price)}
                  </span>
                </div>
                <div className="h-px bg-white/[0.06]" />
                <div className="flex items-center justify-between">
                  <span className="text-green-400 text-[15px] font-semibold flex items-center gap-1.5 tracking-tight">
                    <span className="text-xl">✨</span>
                    {L.discountYourPrice}
                  </span>
                  <span className="text-green-400 text-[24px] font-bold tracking-tight">
                    {formatPrice(Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)))}
                  </span>
                </div>
                <div className="bg-green-500/[0.08] border border-green-500/20 rounded-xl px-3 py-2.5 mt-3">
                  <p className="text-green-400 text-[12px] font-semibold tracking-tight">
                    {L.discountYouSave.replace("{amount}", formatPrice(Math.round(vipPlan.price * VIP_DISCOUNT_RATE)))}
                  </p>
                </div>
              </div>

              {/* Urgency badge */}
              <div className="flex items-center justify-center gap-2 bg-yellow-500/[0.08] border border-yellow-500/20 rounded-full px-4 py-2.5">
                <span className="text-xl">⚡</span>
                <span className="text-yellow-400 text-[12px] font-bold tracking-wider uppercase">
                  {L.discountOneTimeOffer}
                </span>
              </div>

              {/* CTA buttons */}
              <div className="space-y-3 pt-3">
                <button
                  onClick={async () => {
                    setShowDiscountModal(false);
                    setCheckoutPlan("vip");
                    try {
                      localStorage.setItem(DISCOUNT_OFFER_SHOWN_KEY, "true");
                      localStorage.setItem(DISCOUNT_ACCEPTED_KEY, "true");
                      localStorage.setItem(DISCOUNT_ACCEPTED_TIMESTAMP_KEY, Date.now().toString());
                      setDiscountOfferShown(true);
                      setDiscountAccepted(true);
                    } catch (e) {
                      console.error("Error saving discount state:", e);
                    }
                    void trackDiscountOffer("accepted", checkoutEmail.trim() || undefined);
                    if (vipPlan) {
                      // Mirror accepted para ver en admin + Slack
                      capturePremiumEvent("discount_offer_action", {
                        action: "accepted",
                        plan_tier: "vip",
                        original_price: vipPlan.price,
                        final_price: Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                        discount_pct: Math.round(VIP_DISCOUNT_RATE * 100),
                        currency,
                      });
                      trackDiscountOfferActionPostHog(
                        flowName,
                        "accepted",
                        "vip",
                        vipPlan.price,
                        Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                        currency,
                        country
                      );
                    }
                    checkoutOpenSourceRef.current = "discount_accepted";
                    checkoutPanelOpenTimeRef.current = Date.now();
                    if (vipPlan) {
                      trackCheckoutPanelOpenedPostHog(
                        "vip",
                        "discount_accepted",
                        Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                        currency,
                        flowName,
                        country,
                      );
                    }
                    setShowCustomCheckout(true);
                  }}
                  className="w-full py-4 px-6 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 rounded-2xl font-bold text-white text-base shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 hover:scale-[1.02] transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <span>✓</span>
                  <span>{L.discountAccept}</span>
                </button>
                
                <button
                  onClick={async () => {
                    setShowDiscountModal(false);
                    try {
                      localStorage.setItem(DISCOUNT_OFFER_SHOWN_KEY, "true");
                      setDiscountOfferShown(true);
                    } catch (e) {
                      console.error("Error saving discount state:", e);
                    }
                    void trackDiscountOffer("declined");
                    if (vipPlan) {
                      // Mirror declined: es señal de que el usuario vio la oferta y la rechazó
                      capturePremiumEvent("discount_offer_action", {
                        action: "declined",
                        plan_tier: "vip",
                        original_price: vipPlan.price,
                        final_price: Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                        discount_pct: Math.round(VIP_DISCOUNT_RATE * 100),
                        currency,
                      });
                      trackDiscountOfferActionPostHog(
                        flowName,
                        "declined",
                        "vip",
                        vipPlan.price,
                        Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE)),
                        currency,
                        country
                      );
                    }
                  }}
                  className="w-full py-3 text-white/50 hover:text-white/80 text-sm font-medium transition-colors"
                >
                  {L.discountDecline}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Checkout panel ─────────────────────────────────────────────────────── */}
      {showCustomCheckout && (checkoutState === "brick" && brickCheckoutParams && provider === "mercadopago" ? (
        <div className="fixed inset-0 z-[45] flex flex-col overflow-hidden bg-[#0A0E27]">
          <Suspense fallback={
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-white/60 animate-spin" aria-hidden />
              <span className="sr-only">Cargando checkout...</span>
            </div>
          }>
          <MercadoPagoSubscriptionBrick
            amount={brickCheckoutParams.finalPrice}
            packName={brickCheckoutParams.packName}
            buyerEmail={checkoutEmail}
            planTier={brickCheckoutParams.plan}
            flowName={flowName}
            flowTraceId={flowTraceId}
            productId={`${flowName}_${brickCheckoutParams.plan}`}
            country={country}
            successPath={brickCheckoutParams.successPath}
            metadata={{
              source: BRAND_SOURCE_KEY,
              flow_trace_id: flowTraceId,
              plan_tier: brickCheckoutParams.plan,
              template: "BioTemplatePremium",
              template_variant: `mp_brick_${brickCheckoutParams.plan}`,
              checkout_started_at: new Date().toISOString(),
            }}
            onSuccess={(subscriptionId) => {
              // Mirror al funnel antes de redirigir
              capturePremiumEvent("premium_checkout_brick_success", {
                plan_tier: brickCheckoutParams!.plan,
                subscription_id: subscriptionId,
                provider: "mercadopago",
              });
              const base = brickCheckoutParams!.successPath;
              const sep = base.includes("?") ? "&" : "?";
              window.location.href = `${base}${sep}subscription_id=${encodeURIComponent(subscriptionId)}`;
            }}
            onBack={() => {
              // Mirror: usuario canceló desde dentro del brick
              capturePremiumEvent("checkout_panel_closed", {
                plan_tier: brickCheckoutParams?.plan,
                close_reason: "brick_back",
                provider: "mercadopago",
              });
              capturePremiumEvent("checkout_abandonment", {
                plan_tier: brickCheckoutParams?.plan,
                abandonment_stage: "mp_brick",
                provider: "mercadopago",
              });
              setBrickCheckoutParams(null);
              setCheckoutState("idle");
            }}
            onError={() => {
              // Mirror error crítico del brick
              capturePremiumEvent("premium_checkout_brick_error", {
                plan_tier: brickCheckoutParams?.plan,
                provider: "mercadopago",
                email_provided: !!checkoutEmail.trim(),
              });
              if (checkoutEmail && brickCheckoutParams) {
                void fetch("/api/send-checkout-recovery-email", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    buyerEmail: checkoutEmail,
                    buyerName: checkoutEmail.split("@")[0],
                    checkoutUrl: window.location.href,
                    planName: brickCheckoutParams!.packName,
                    price: brickCheckoutParams!.finalPrice,
                    currency: "ARS",
                    flowTraceId,
                  }),
                }).catch((emailErr) => {
                  console.error("[BioTemplatePremium] Failed to send MP recovery email:", emailErr);
                });
              }
            }}
          />
          </Suspense>
        </div>
      ) : (
        <div
          className={`fixed inset-0 z-[45] flex min-h-[100dvh] flex-col overflow-hidden ${
            mercadoPagoFormCheckoutOpen ? "bg-[#0A0E27]" : ""
          }`}
        >
          {mercadoPagoFormCheckoutOpen ? (
            <div className="absolute inset-0 bg-[#0A0E27]" aria-hidden />
          ) : (
            <>
              <div
                className="absolute inset-0"
                style={
                  checkoutMediaFullBleed
                    ? {
                        backgroundImage: `url(${standardCheckoutBgGif})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }
                    : resolvedBackgroundUrl
                      ? {
                          backgroundImage: `url(${resolvedBackgroundUrl})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        }
                      : provider === "mercadopago"
                        ? {
                            backgroundImage: "linear-gradient(160deg, #009EE3 0%, #0077B5 45%, #0A0E27 100%)",
                          }
                        : isImmediateDiscount
                          ? {
                              backgroundColor: "#000000",
                            }
                          : {
                              backgroundImage: "linear-gradient(160deg, #1e1b4b 0%, #312e81 40%, #0A0E27 100%)",
                            }
                }
              />
              <div
                className={`absolute inset-0 ${
                  provider === "mercadopago"
                    ? "bg-black/50 backdrop-blur-sm"
                    : isImmediateDiscount
                      ? "bg-black/80"
                      : "bg-black/70 backdrop-blur-sm"
                }`}
              />
            </>
          )}
          <div
            className={`relative z-10 flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden animate-fade-in ${
              mercadoPagoFormCheckoutOpen
                ? "min-h-[100dvh] items-stretch justify-start p-0"
                : "items-start justify-center p-0 md:items-center md:p-6"
            }`}
          >
            <div
              className={`relative flex w-full max-w-none flex-col ${
                mercadoPagoFormCheckoutOpen
                  ? "min-h-[100dvh] flex-1 rounded-none bg-[#0A0E27] border-0 shadow-none md:min-h-0 md:flex-none md:max-w-md md:rounded-3xl md:my-6 md:overflow-hidden md:shadow-2xl md:border-x md:border-white/[0.08]"
                  : "md:max-w-md md:rounded-3xl md:overflow-hidden md:shadow-2xl md:my-6"
              } ${
                mercadoPagoFormCheckoutOpen
                  ? ""
                  : isImmediateDiscount && provider === "stripe"
                    ? "border-x border-amber-500/30 md:border md:border-amber-500/40 md:shadow-amber-500/20"
                    : provider === "mercadopago" && standardCheckoutBgGif
                      ? "border-x border-[#009EE3]/30 md:border md:border-[#009EE3]/40"
                      : "bg-[#0A0E27]/95 border-x border-white/[0.05] md:border md:border-white/[0.08] md:shadow-black/40"
              }`}
              style={
                mercadoPagoFormCheckoutOpen
                  ? undefined
                  : checkoutMediaFullBleed
                    ? undefined
                    : standardCheckoutBgGif &&
                        ((isImmediateDiscount && checkoutPlan === "standard") || provider === "mercadopago") &&
                        checkoutOpenSourceRef.current !== "video_cta"
                      ? {
                          backgroundImage: `url(${standardCheckoutBgGif})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        }
                      : undefined
              }
            >
              {/* Overlay sobre la card solo si el GIF va dentro de la card (no full-bleed) */}
              {!checkoutMediaFullBleed &&
                !mercadoPagoFormCheckoutOpen &&
                standardCheckoutBgGif &&
                ((isImmediateDiscount && checkoutPlan === "standard") || provider === "mercadopago") &&
                checkoutOpenSourceRef.current !== "video_cta" && (
                  <div className="absolute inset-0 bg-black/50 md:rounded-3xl" />
                )}
            <div className={`flex-shrink-0 border-b relative z-10 ${
              mercadoPagoFormCheckoutOpen
                ? "border-white/[0.08] bg-[#0A0E27]"
                : isImmediateDiscount && provider === "stripe" && checkoutPlan === "standard" && standardCheckoutBgGif && checkoutOpenSourceRef.current !== "video_cta"
                  ? "border-amber-500/20 bg-black/40"
                  : isImmediateDiscount && provider === "stripe"
                    ? "border-amber-500/20 bg-[#0A0E27]"
                    : provider === "mercadopago" && standardCheckoutBgGif && checkoutOpenSourceRef.current !== "video_cta"
                      ? "border-[#009EE3]/20 bg-black/40"
                      : "border-white/[0.08] bg-[#0A0E27]"
            }`}>
              <div className="flex items-center justify-between px-4 h-14">
                <button
                  type="button"
                  onClick={() => {
                    const timeInCheckout = checkoutPanelOpenTimeRef.current
                      ? Math.round((Date.now() - checkoutPanelOpenTimeRef.current) / 1000)
                      : 0;
                    const emailProvided = !!checkoutEmail.trim();
                    // Mirror to funnel_events + Slack so panel-close is visible in admin
                    capturePremiumEvent("checkout_panel_closed", {
                      plan_tier: checkoutPlan,
                      email_provided: emailProvided,
                      time_in_checkout_s: timeInCheckout,
                      close_reason: "user_closed",
                    });
                    capturePremiumEvent("checkout_abandonment", {
                      plan_tier: checkoutPlan,
                      email_provided: emailProvided,
                      time_in_checkout_s: timeInCheckout,
                      abandonment_stage: emailProvided ? "email_entered" : "no_email",
                    });
                    trackCheckoutPanelClosedPostHog(
                      checkoutPlan,
                      emailProvided,
                      timeInCheckout,
                      "user_closed",
                      flowName,
                      country,
                    );
                    trackCheckoutAbandonmentPostHog(
                      checkoutPlan,
                      emailProvided,
                      timeInCheckout,
                      flowName,
                      country,
                    );
                    checkoutPanelOpenTimeRef.current = null;
                    setShowCustomCheckout(false);
                    setCheckoutError(null);
                    // Reset plan to standard when closing checkout
                    setCheckoutPlan("standard");
                  }}
                  className={`flex items-center gap-1 min-h-[44px] min-w-[44px] -ml-2 pl-2 active:opacity-60 transition-opacity ${
                    provider === "mercadopago" 
                      ? "text-[#009EE3]" 
                      : isImmediateDiscount 
                        ? "text-amber-400" 
                        : "text-violet-300"
                  }`}
                  aria-label={L.checkoutBack}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-[17px] font-normal">{L.checkoutBack}</span>
                </button>
                <h2 className="text-[17px] font-semibold text-white tracking-tight">{L.checkoutTitle}</h2>
                <div className="w-20 flex-shrink-0" />
              </div>
            </div>

            <div className={`flex-shrink-0 px-4 py-3 relative z-10 ${
              provider === "mercadopago" 
                ? "bg-gradient-to-b from-[#009EE3] to-[#0077B5]" 
                : isImmediateDiscount 
                  ? "bg-gradient-to-r from-amber-600/80 via-yellow-500/80 to-amber-600/80" 
                  : "bg-gradient-to-b from-violet-500 to-indigo-600"
            }`}>
              <div className="flex items-center justify-center gap-2">
                <Lock className={`w-4 h-4 ${isImmediateDiscount && provider === "stripe" ? "text-black" : "text-white"}`} />
                <span className={`text-sm font-semibold tracking-tight ${isImmediateDiscount && provider === "stripe" ? "text-black" : "text-white"}`}>
                  {provider === "mercadopago"
                    ? L.securePaymentMP
                    : isImmediateDiscount
                      ? L.specialOfferPayment
                      : L.securePaymentStripe}
                </span>
              </div>
            </div>

            <div className={`flex-1 relative z-10 ${
              mercadoPagoFormCheckoutOpen
                ? "bg-[#0A0E27] min-h-0 flex flex-col"
                : ((isImmediateDiscount && provider === "stripe" && checkoutPlan === "standard" && standardCheckoutBgGif) || (provider === "mercadopago" && standardCheckoutBgGif)) && checkoutOpenSourceRef.current !== "video_cta"
                  ? "bg-black/50"
                  : "bg-[#0A0E27]"
            }`}>
              <div className="px-4 py-5 space-y-4 pb-8">
                {provider === "mercadopago" && (
                  <div className="rounded-2xl p-3 bg-[#009EE3]/10 border border-[#009EE3]/35">
                    <p className="text-[#7FD7FF] text-[12px] font-semibold tracking-tight">
                      {L.checkoutStep1}
                    </p>
                    <p className="text-white/70 text-[12px] mt-1 tracking-tight">
                      {L.checkoutStep1Desc}
                    </p>
                  </div>
                )}

                <div className={`rounded-2xl p-3 sm:p-4 border ${
                  !mercadoPagoFormCheckoutOpen &&
                  ((provider === "mercadopago" && standardCheckoutBgGif) || (isImmediateDiscount && checkoutPlan === "standard" && standardCheckoutBgGif)) &&
                  checkoutOpenSourceRef.current !== "video_cta"
                    ? "bg-black/40 border-[#009EE3]/20 backdrop-blur-sm"
                    : provider === "mercadopago"
                      ? "bg-[#1C1F3A] border-[#009EE3]/25"
                      : isImmediateDiscount 
                        ? "bg-black/40 border-amber-500/20 backdrop-blur-sm" 
                        : "bg-[#1C1F3A] border-white/[0.06]"
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-white/50 text-xs sm:text-[13px] font-medium tracking-tight">{L.checkoutProduct}</p>
                      <h3 className="text-white text-[15px] sm:text-[16px] font-semibold tracking-tight leading-snug mt-1">
                        {activePlan.productName}
                        {provider === "stripe" && billingInterval === "quarterly" && (
                          <span className="ml-2 text-amber-400 text-[11px] sm:text-[12px] font-bold">
                            (3 {L.months})
                          </span>
                        )}
                      </h3>
                      <p className="text-white/45 text-[11px] sm:text-[12px] mt-1">
                        {provider === "stripe" && billingInterval === "quarterly"
                          ? L.checkoutDescQuarterly
                          : checkoutDescLabel}
                      </p>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                      {(() => {
                        const isQuarterlyStripe = provider === "stripe" && billingInterval === "quarterly";
                        const basePrice = discountAccepted ? Math.round(activePlan.price * (1 - VIP_DISCOUNT_RATE)) : activePlan.price;
                        const displayPrice = isQuarterlyStripe 
                          ? Math.round(basePrice * (1 - QUARTERLY_DISCOUNT_RATE) * 3)
                          : (discountAccepted ? Math.round(activePlan.price * (1 - VIP_DISCOUNT_RATE)) : activePlan.price);
                        const originalPrice = isQuarterlyStripe ? basePrice * 3 : activePlan.price;
                        const showStrikethrough = discountAccepted || isQuarterlyStripe;
                        
                        return (
                          <>
                            {showStrikethrough && (
                              <div className="text-white/45 line-through text-xs sm:text-[13px] font-medium">
                                {formatPrice(originalPrice)}
                              </div>
                            )}
                            <div className={`text-xl sm:text-[22px] font-bold tracking-tight ${showStrikethrough ? (isQuarterlyStripe ? "text-amber-400" : "text-green-400") : "text-white"}`}>
                              {formatPrice(displayPrice)}
                            </div>
                            <div className="text-white/35 text-[11px] sm:text-[12px]">
                              {isQuarterlyStripe ? L.checkoutPerThreeMonths : L.checkoutPerMonth}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Selector de duración - Solo para Stripe/USD */}
                {provider === "stripe" && (
                  <div className="bg-[#1C1F3A] rounded-2xl p-3 sm:p-4 border border-white/[0.06]">
                    <p className="text-white/50 text-xs sm:text-[13px] font-medium tracking-tight mb-3">
                      {L.subscriptionDuration}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {/* Opción Mensual */}
                      <button
                        type="button"
                        onClick={() => setBillingInterval("monthly")}
                        className={`relative p-3 sm:p-4 rounded-xl border-2 transition-all min-h-[100px] sm:min-h-[120px] ${
                          billingInterval === "monthly"
                            ? "border-violet-500 bg-violet-500/10"
                            : "border-white/15 bg-transparent hover:border-white/30"
                        }`}
                      >
                        <div className="text-center flex flex-col justify-center h-full">
                          <p className={`text-sm sm:text-base font-semibold ${billingInterval === "monthly" ? "text-violet-300" : "text-white/80"}`}>
                            {L.monthly}
                          </p>
                          <p className={`text-xl sm:text-2xl font-bold mt-1 sm:mt-2 ${billingInterval === "monthly" ? "text-white" : "text-white/70"}`}>
                            {formatPrice(discountAccepted ? Math.round(activePlan.price * (1 - VIP_DISCOUNT_RATE)) : activePlan.price)}
                          </p>
                          <p className="text-white/40 text-[10px] sm:text-[11px] mt-0.5">{L.checkoutPerMonth}</p>
                        </div>
                        {billingInterval === "monthly" && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-4 h-4 text-violet-400" />
                          </div>
                        )}
                      </button>

                      {/* Opción Trimestral */}
                      <button
                        type="button"
                        onClick={() => setBillingInterval("quarterly")}
                        className={`relative p-3 sm:p-4 rounded-xl border-2 transition-all min-h-[100px] sm:min-h-[120px] ${
                          billingInterval === "quarterly"
                            ? "border-amber-500 bg-gradient-to-br from-amber-500/20 to-orange-500/10 shadow-lg shadow-amber-500/20"
                            : "border-white/15 bg-transparent hover:border-amber-500/50 hover:bg-amber-500/5"
                        }`}
                      >
                        {/* Badge de descuento extra + Mejor valor */}
                        <div className="absolute -top-2.5 sm:-top-3 left-1/2 -translate-x-1/2 flex gap-1">
                          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[9px] sm:text-[10px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full whitespace-nowrap shadow-lg">
                            🔥 {L.bestValue}
                          </span>
                        </div>
                        <div className="text-center mt-2 sm:mt-3 flex flex-col justify-center">
                          <p className={`text-sm sm:text-base font-bold ${billingInterval === "quarterly" ? "text-amber-300" : "text-white/80"}`}>
                            {L.quarterly}
                          </p>
                          <p className="text-white/50 text-[10px] sm:text-[11px] mb-1">
                            {L.threeMonths}
                          </p>
                          {(() => {
                            const basePrice = discountAccepted ? Math.round(activePlan.price * (1 - VIP_DISCOUNT_RATE)) : activePlan.price;
                            const quarterlyPrice = Math.round(basePrice * (1 - QUARTERLY_DISCOUNT_RATE) * 3);
                            const originalQuarterly = basePrice * 3;
                            const savings = originalQuarterly - quarterlyPrice;
                            return (
                              <>
                                <div className="text-white/40 line-through text-[11px] sm:text-xs">
                                  {formatPrice(originalQuarterly)}
                                </div>
                                <p className={`text-xl sm:text-2xl font-bold ${billingInterval === "quarterly" ? "text-amber-300" : "text-white/70"}`}>
                                  {formatPrice(quarterlyPrice)}
                                </p>
                                <p className="text-green-400 text-[10px] sm:text-[11px] font-semibold mt-0.5">
                                  {L.discountYouSave.replace("{amount}", formatPrice(savings))}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                        {billingInterval === "quarterly" && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-4 h-4 text-amber-400" />
                          </div>
                        )}
                      </button>
                    </div>
                    
                    {/* Mensaje informativo sobre la selección */}
                    <div className={`mt-3 p-2 rounded-lg text-center transition-all ${
                      billingInterval === "quarterly" 
                        ? "bg-amber-500/10 border border-amber-500/30" 
                        : "bg-violet-500/10 border border-violet-500/20"
                    }`}>
                      {billingInterval === "quarterly" ? (
                        <p className="text-amber-300/90 text-[10px] sm:text-[11px] leading-relaxed">
                          {L.quarterlyExtraDiscount.replace("{pct}", String(Math.round(QUARTERLY_DISCOUNT_RATE * 100)))}
                        </p>
                      ) : (
                        <p className="text-violet-300/70 text-[10px] sm:text-[11px] leading-relaxed">
                          {L.monthlyAutorenew}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-[#1C1F3A] rounded-2xl p-4 border border-white/[0.06] space-y-2">
                  <label htmlFor="checkout-email-premium" className="block text-white text-[14px] font-semibold tracking-tight">
                    Email <span className={provider === "mercadopago" ? "text-[#36C5FF]" : "text-violet-300"}>*</span>
                  </label>
                  <input
                    id="checkout-email-premium"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={checkoutEmail}
                    onFocus={() => {
                      trackEmailInputInteractionPostHog("focus", checkoutPlan, undefined, flowName, country);
                    }}
                    onChange={(e) => {
                      setCheckoutEmail(e.target.value);
                      if (emailTouched) validateEmail(e.target.value);
                    }}
                    onBlur={() => {
                      setEmailTouched(true);
                      trackEmailInputInteractionPostHog("blur", checkoutPlan, !!checkoutEmail.trim(), flowName, country);
                      validateEmail(checkoutEmail);
                    }}
                    placeholder={L.emailPlaceholder}
                    className={`w-full bg-[#0A0E27] border rounded-xl px-4 py-3 text-white placeholder-white/35 focus:outline-none focus:ring-2 ${
                      provider === "mercadopago" ? "focus:ring-[#009EE3]/70" : "focus:ring-violet-500/60"
                    } focus:border-transparent transition-all text-[15px] ${
                      emailError && emailTouched
                        ? "border-red-500/50 focus:ring-red-500/60"
                        : "border-white/15 hover:border-white/25"
                    }`}
                  />
                  {emailError && emailTouched && (
                    <p className="text-red-400 text-[12px] mt-1 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {emailError}
                    </p>
                  )}
                  <p className="text-white/35 text-[11px]">
                    {L.emailReceiptNote}
                  </p>
                </div>

                {checkoutError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
                    <p 
                      className="text-red-300 text-sm"
                      dangerouslySetInnerHTML={{ __html: checkoutError }}
                    />
                    <button
                      type="button"
                      onClick={() => setCheckoutError(null)}
                      className="text-white/60 text-xs underline mt-1"
                    >
                      {L.checkoutClose}
                    </button>
                  </div>
                )}

                <button
                  onClick={() => handleCheckout()}
                  onMouseEnter={() => preloadMercadoPagoOnHover(provider)}
                  onFocus={() => preloadMercadoPagoOnHover(provider)}
                  disabled={checkoutState === "creating" || checkoutState === "redirecting" || checkoutState === "brick"}
                  className={`w-full py-4 px-4 bg-gradient-to-r ${
                    provider === "mercadopago"
                      ? "from-[#009EE3] to-[#007EB5]"
                      : discountAccepted
                        ? "from-green-500 to-emerald-500"
                        : tierConfig.gradient
                  } rounded-xl font-bold text-white shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all`}
                >
                  {checkoutState === "creating" ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />{L.checkoutPreparing}</>
                  ) : checkoutState === "redirecting" ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />{L.checkoutRedirecting} {gatewayLabel}...</>
                  ) : provider === "mercadopago" ? (
                    <span>{L.continueToCardPayment}</span>
                  ) : (
                    (() => {
                      const isQuarterlyStripe = billingInterval === "quarterly";
                      const basePrice = discountAccepted ? Math.round(activePlan.price * (1 - VIP_DISCOUNT_RATE)) : activePlan.price;
                      const finalPrice = isQuarterlyStripe 
                        ? Math.round(basePrice * (1 - QUARTERLY_DISCOUNT_RATE) * 3)
                        : basePrice;
                      const periodLabel = isQuarterlyStripe
                        ? ` ${L.checkoutPerThreeMonths}`
                        : L.checkoutPerMonth;
                      return <span>{L.checkoutPay} — {formatPrice(finalPrice)}{periodLabel}</span>;
                    })()
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-white/30 text-[12px] pt-1">
                  <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="font-medium">
                    {L.secureEncryptedPayment}
                  </span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      ) )}

      {/* ── Background ─────────────────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0" aria-hidden>
        {/* Base negro */}
        <div className="absolute inset-0 bg-black" />
        {/* Imagen de fondo */}
        {resolvedBackgroundUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${resolvedBackgroundUrl})` }}
          />
        )}
        {/* Overlay - 40% en Special Offer para ver mejor el background */}
        <div className={`absolute inset-0 ${
          isImmediateDiscount 
            ? "bg-gradient-to-b from-black/40 via-black/40 to-black/50" 
            : "bg-gradient-to-b from-black/50 via-black/60 to-black/75"
        }`} />
        {/* Tint dorado elegante en Special Offer */}
        <div className={`absolute inset-0 ${
          isImmediateDiscount 
            ? "bg-gradient-to-br from-amber-500/15 via-yellow-600/10 to-orange-500/15"
            : `bg-gradient-to-br ${tierConfig.gradient} opacity-[0.06]`
        }`} />
        {/* Fire effect overlay - Solo en Special Offer */}
        {isImmediateDiscount && (
          <>
            {/* Animated fire glow from bottom */}
            <div className="absolute inset-0 bg-gradient-to-t from-orange-600/20 via-red-500/10 to-transparent animate-fire-pulse" />
            {/* Floating fire particles effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute bottom-0 left-1/4 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl animate-fire-float" />
              <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-red-500/10 rounded-full blur-3xl animate-fire-float animation-delay-1000" />
              <div className="absolute bottom-0 left-1/2 w-36 h-36 bg-yellow-500/10 rounded-full blur-3xl animate-fire-float animation-delay-2000" />
            </div>
          </>
        )}
      </div>

      {/* ── Contenido principal ───────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden">
        <div className="min-h-[100dvh] flex items-start justify-center px-3 sm:px-4 py-6 sm:py-8 md:py-10 lg:py-12 pb-10 md:pb-14">
          <div className="w-full max-w-[min(100%,22rem)] sm:max-w-[24rem] md:max-w-[26rem] flex flex-col items-center text-center mx-auto">

          {/* Badge principal - Oferta Especial (estilo Black Friday) vs elegir membresía */}
          <div className="flex justify-center mb-2.5 sm:mb-3 md:mb-4 shrink-0">
            <div
              className={`inline-flex items-center gap-1.5 md:gap-2 rounded-full ${
                isImmediateDiscount
                  ? "relative px-3.5 md:px-5 py-1.5 md:py-2 bg-black/95 border-2 border-amber-500/80 shadow-[0_0_28px_rgba(245,158,11,0.35),inset_0_1px_0_rgba(253,230,138,0.12)] ring-1 ring-yellow-500/25"
                  : "px-3 md:px-4 py-1 md:py-1.5 border bg-white/5 border-white/20 shadow-lg"
              }`}
            >
              {isImmediateDiscount && (
                <span
                  className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-amber-500/10"
                  aria-hidden
                />
              )}
              <TierIcon
                className={`relative z-[1] w-3.5 h-3.5 md:w-4 md:h-4 shrink-0 ${
                  isImmediateDiscount ? "text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" : "text-white/80"
                }`}
              />
              <span
                className={`relative z-[1] inline-block text-[11px] md:text-xs font-extrabold tracking-wide md:tracking-wider uppercase ${
                  isImmediateDiscount
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-200 to-amber-300"
                    : "text-white/80"
                }`}
              >
                {isImmediateDiscount ? L.specialOfferLimited : L.chooseMembership}
              </span>
            </div>
          </div>

          {/* Countdown de la ventana Special Offer o nada si expiró / sin START */}
          {isImmediateDiscount && timeRemaining > 0 ? (
            <div className="mb-3 sm:mb-4 md:mb-5 w-full shrink-0">
              <div className="relative">
                <div className="absolute -inset-1.5 rounded-2xl bg-gradient-to-r from-amber-600/40 via-yellow-500/35 to-amber-600/40 blur-lg opacity-90" />
                <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-amber-500/20 to-transparent pointer-events-none" />
                <div className="relative rounded-xl border-2 border-amber-500/70 bg-zinc-950 p-3 md:p-4 shadow-[0_0_32px_rgba(0,0,0,0.85),0_0_24px_rgba(245,158,11,0.15)] ring-1 ring-black/80">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs md:text-sm font-extrabold uppercase tracking-wide text-amber-200 drop-shadow-[0_0_12px_rgba(251,191,36,0.35)]">
                      {resolveOfferEndsInLabel(L.offerEndsIn, language)}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1 rounded-lg border border-amber-500/40 bg-black py-2 md:py-2.5 px-4 md:px-5 shadow-inner shadow-black/60">
                    <span className="font-body text-2xl md:text-3xl font-black tabular-nums tracking-tight text-white drop-shadow-[0_0_14px_rgba(250,204,21,0.25)]">
                      {formatTimeRemaining(timeRemaining)}
                    </span>
                  </div>
                  <div className="mt-2 text-center text-[11px] md:text-xs font-extrabold uppercase tracking-widest text-amber-400/95">
                    {L.dontMissOut}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Enlaces apilados (estilo Linktree): una card debajo de otra */}
          <div className="w-full flex flex-col gap-3.5 sm:gap-4 md:gap-5">
            {/* Card Free — Telegram público (sin checkout) */}
            {resolvedFreeTelegramUrl ? (
              <div
                className={`rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 backdrop-blur-sm relative border shadow-lg w-full min-w-0 ${
                  isImmediateDiscount
                    ? "bg-sky-950/40 border-sky-400/35"
                    : "bg-sky-950/25 border-sky-500/25"
                }`}
              >
                <div className="flex items-center justify-center gap-2 mb-1.5 sm:mb-2">
                  <ExternalLink className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-sky-300" aria-hidden />
                  <span className="font-semibold text-sm md:text-base text-white">{L.freePlanTitle}</span>
                </div>
                <p className="text-[11px] sm:text-xs md:text-sm text-sky-100/85 text-center leading-snug sm:leading-relaxed mb-3 sm:mb-3.5 md:mb-4 px-0.5">
                  {L.freePlanSubtitle}
                </p>
                <a
                  href={resolvedFreeTelegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={L.freePlanCta}
                  className="flex w-full min-h-[44px] sm:min-h-0 items-center justify-center gap-2 py-2.5 sm:py-3 md:py-3.5 px-3 sm:px-4 md:px-5 rounded-xl font-semibold text-sm md:text-base shadow-lg transition-all active:scale-[0.98] bg-sky-500 text-white hover:bg-sky-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
                  onClick={() => {
                    trackPlanButtonClickPostHog("free", 0, currency, flowName, country, false);
                    // Server-side mirror → Slack + /admin/live-funnel.
                    // Includes the OFFER context the visitor was looking at when they
                    // chose the free path — so we can answer "did the discount price
                    // visible on Standard scare them off into Free?".
                    capturePremiumEvent("free_telegram_click", {
                      plan_tier: "free",
                      url: resolvedFreeTelegramUrl,
                      currency,
                      provider,
                      // Offer context (what the user SAW on the page at click time)
                      is_special_offer: isImmediateDiscount,
                      is_recovery_discount: discountAccepted,
                      discount_pct:
                        isImmediateDiscount || discountAccepted
                          ? Math.round(VIP_DISCOUNT_RATE * 100)
                          : 0,
                      standard_original_price: standardPlan.price,
                      standard_final_price:
                        isImmediateDiscount || discountAccepted
                          ? Math.round(standardPlan.price * (1 - VIP_DISCOUNT_RATE))
                          : standardPlan.price,
                      vip_original_price: vipPlan?.price ?? null,
                      vip_disabled: vipPlan?.isDisabled ?? null,
                    });
                  }}
                >
                  {L.freePlanCta}
                </a>
              </div>
            ) : null}

            {/* Card Standard */}
            <div className={`rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 backdrop-blur-sm relative w-full min-w-0 ${
              (isImmediateDiscount || discountAccepted)
                ? "bg-zinc-950/60 border border-white/20 shadow-xl" 
                : "bg-white/[0.03] border border-white/10 shadow-lg"
            }`}>
              {/* Badge - Solo en recovery modal con descuento aceptado (NO en Special Offer) */}
              {!isImmediateDiscount && discountAccepted && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-white/30 to-gray-300/30 rounded-full blur-md" />
                    <div className="relative flex items-center gap-1.5 bg-gradient-to-r from-zinc-900 via-black to-zinc-900 border border-white/40 px-3 py-1 rounded-full shadow-lg">
                      <Sparkles className="w-3 h-3 text-white" />
                      <span className="text-[10px] font-bold tracking-wider text-white uppercase">
                        {L.exclusiveDiscount}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="font-semibold text-sm md:text-base text-white">{L.channelLabel}</span>
              </div>
              {standardPlan.subtitle && (
                <p className={`text-xs md:text-sm mb-3 md:mb-4 text-center leading-relaxed ${
                  (isImmediateDiscount || discountAccepted) ? "text-gray-300" : "text-gray-400"
                }`}>{standardPlan.subtitle}</p>
              )}
              
              {/* Precio Standard — visible siempre.
                  · Sin oferta: solo el precio + unidad ("$29.99 USD/mo").
                  · Con oferta (Special Offer global o recovery aceptada):
                    precio original tachado + precio final grande + badge "30% OFF".
                  Visual hierarchy: el número final es lo más prominente; el original
                  queda como contexto, el badge da el por qué. tabular-nums alinea
                  los dígitos para que no "salten" en hover/animación. */}
              {(() => {
                const hasOffer = isImmediateDiscount || discountAccepted;
                const finalPrice = hasOffer
                  ? Math.round(standardPlan.price * (1 - VIP_DISCOUNT_RATE))
                  : standardPlan.price;
                const discountPct = Math.round(VIP_DISCOUNT_RATE * 100);
                // Unidad compacta tipo "USD/mo" o "ARS/mes" (no la frase completa
                // de L.checkoutPerMonth — esa es para el panel de checkout).
                const unitShort = currency === "USD" ? "USD/mo" : "ARS/mes";
                return (
                  <div className="mb-3 md:mb-4 flex flex-col items-center gap-1">
                    {hasOffer ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-white/45 line-through decoration-white/30 decoration-[1.5px] text-sm md:text-base font-medium tabular-nums">
                            {formatPrice(standardPlan.price)}
                          </span>
                          <span className="text-white text-2xl md:text-[28px] font-bold tracking-tight tabular-nums leading-none">
                            {formatPrice(finalPrice)}
                          </span>
                          <span className="text-white/55 text-[10px] md:text-[11px] font-medium uppercase tracking-wide">
                            {unitShort}
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] md:text-[11px] font-bold tracking-wide text-emerald-300 ring-1 ring-emerald-400/40">
                          <Sparkles className="w-3 h-3" aria-hidden />
                          {discountPct}% OFF
                        </span>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-white text-2xl md:text-[28px] font-bold tracking-tight tabular-nums leading-none">
                          {formatPrice(standardPlan.price)}
                        </span>
                        <span className="text-white/55 text-[10px] md:text-[11px] font-medium uppercase tracking-wide">
                          {unitShort}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <button
                onMouseEnter={() => preloadMercadoPagoOnHover(provider)}
                onFocus={() => preloadMercadoPagoOnHover(provider)}
                onClick={() => {
                  const hasOffer = isImmediateDiscount || discountAccepted;
                  const price = hasOffer
                    ? Math.round(standardPlan.price * (1 - VIP_DISCOUNT_RATE))
                    : standardPlan.price;

                  trackPlanButtonClickPostHog(
                    "standard",
                    price,
                    currency,
                    flowName,
                    country,
                    isImmediateDiscount,
                  );
                  // Server-side mirror — same event the funnel notifier looks for
                  // (plan_button_click is in ALWAYS_NOTIFY). Carries the full offer
                  // breakdown so #anaafernandez sees "AR · standard · $6.99 → $4.89
                  // (30% OFF, special_offer) · /invitacion-premium" at a glance.
                  capturePremiumEvent("plan_button_click", {
                    plan_tier: "standard",
                    original_price: standardPlan.price,
                    final_price: price,
                    discount_pct: hasOffer ? Math.round(VIP_DISCOUNT_RATE * 100) : 0,
                    is_special_offer: isImmediateDiscount,
                    is_recovery_discount: discountAccepted,
                    has_offer: hasOffer,
                    currency,
                    gateway: provider,
                  });
                  setCheckoutPlan("standard");
                  checkoutOpenSourceRef.current = "direct_button";
                  checkoutPanelOpenTimeRef.current = Date.now();
                  trackCheckoutPanelOpenedPostHog(
                    "standard",
                    "direct_button",
                    price,
                    currency,
                    flowName,
                    country,
                  );
                  setShowCustomCheckout(true);
                }}
                className={`w-full min-h-[44px] sm:min-h-0 py-2.5 sm:py-3 md:py-3.5 px-3 sm:px-4 md:px-5 rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] ${
                  (isImmediateDiscount || discountAccepted)
                    ? "bg-white text-black hover:bg-gray-100"
                    : "bg-white/90 text-black hover:bg-white"
                }`}
              >
                {ctaText}
              </button>
            </div>

            {/* Card VIP (solo si se configura) */}
            {vipPlan && (
              <div
                className={`rounded-xl md:rounded-2xl p-3.5 sm:p-4 md:p-5 backdrop-blur-sm relative w-full min-w-0 ${
                  vipPlan.isDisabled
                    ? "bg-gray-900/20 border border-gray-600/20 shadow-sm opacity-60"
                    : isImmediateDiscount
                      ? "border-2 border-amber-500/65 bg-gradient-to-b from-zinc-950 via-black to-zinc-950 shadow-[0_0_36px_rgba(245,158,11,0.22),0_12px_40px_rgba(0,0,0,0.65)] ring-1 ring-yellow-500/20"
                      : "bg-white/[0.03] border border-amber-500/20 shadow-lg"
                }`}
              >
                {/* Badge - Solo si está habilitado */}
                {!vipPlan.isDisabled && (isImmediateDiscount || discountAccepted) && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                    <div className="relative">
                      <div
                        className={`absolute -inset-1 rounded-full blur-md ${
                          isImmediateDiscount
                            ? "bg-gradient-to-r from-amber-400/55 via-yellow-400/45 to-amber-500/55"
                            : "bg-gradient-to-r from-green-500/40 to-emerald-500/40"
                        }`}
                      />
                      <div
                        className={`relative flex items-center gap-1.5 px-3 py-1 rounded-full shadow-lg border ${
                          isImmediateDiscount
                            ? "border-amber-400/80 bg-black/95 text-amber-200"
                            : "border-transparent bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                        }`}
                      >
                        <Sparkles
                          className={`w-3 h-3 shrink-0 ${isImmediateDiscount ? "text-amber-300" : "text-white"}`}
                        />
                        <span
                          className={`inline-block text-[10px] font-extrabold tracking-wider uppercase ${
                            isImmediateDiscount
                              ? "text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-200"
                              : "text-white"
                          }`}
                        >
                          {isImmediateDiscount ? L.specialOffer : L.exclusiveDiscount}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Badge de "Capacidad Completa" si está deshabilitado */}
                {vipPlan.isDisabled && (
                  <div className="absolute -top-2 md:-top-2.5 left-1/2 -translate-x-1/2 z-10 max-w-[90%]">
                    <div className="flex items-center gap-1 px-2 py-0.5 md:gap-1.5 md:px-3 md:py-1 rounded-full bg-gray-700/80 border border-gray-600/50 shadow-lg">
                      <Lock className="w-2.5 h-2.5 md:w-3 md:h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-[9px] md:text-[10px] font-bold tracking-wide md:tracking-wider text-gray-300 uppercase whitespace-nowrap">
                        {L.vipCapacityFull}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown
                    className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${
                      vipPlan.isDisabled
                        ? "text-gray-500"
                        : isImmediateDiscount
                          ? "text-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.45)]"
                          : "text-amber-400"
                    }`}
                  />
                  <span
                    className={`inline-block font-bold text-sm md:text-base tracking-tight ${
                      vipPlan.isDisabled
                        ? "text-gray-500"
                        : isImmediateDiscount
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-200"
                          : "text-white"
                    }`}
                  >
                    VIP
                  </span>
                </div>
                {vipPlan.subtitle && (
                  <p
                    className={`text-xs md:text-sm leading-relaxed mb-3 md:mb-4 text-center px-1 ${
                      vipPlan.isDisabled
                        ? "text-gray-600"
                        : isImmediateDiscount
                          ? "text-amber-100/90 font-medium"
                          : "text-gray-400"
                    }`}
                  >
                    {vipPlan.subtitle}
                  </p>
                )}

                <button
                  onMouseEnter={() => !vipPlan.isDisabled && preloadMercadoPagoOnHover(provider)}
                  onFocus={() => !vipPlan.isDisabled && preloadMercadoPagoOnHover(provider)}
                  onClick={() => {
                    if (vipPlan.isDisabled) return;

                    const hasOffer = isImmediateDiscount || discountAccepted;
                    const price = hasOffer
                      ? Math.round(vipPlan.price * (1 - VIP_DISCOUNT_RATE))
                      : vipPlan.price;

                    trackPlanButtonClickPostHog(
                      "vip",
                      price,
                      currency,
                      flowName,
                      country,
                      !!vipPlan.playbackId,
                    );
                    // Server-side mirror — Slack + /admin/live-funnel sees
                    // VIP intent with the same offer breakdown as Standard,
                    // plus whether the user is about to see the VIP video
                    // (which means the discount modal can still pop later)
                    // or be sent straight to the exclusive landing page.
                    capturePremiumEvent("plan_button_click", {
                      plan_tier: "vip",
                      original_price: vipPlan.price,
                      final_price: price,
                      discount_pct: hasOffer ? Math.round(VIP_DISCOUNT_RATE * 100) : 0,
                      is_special_offer: isImmediateDiscount,
                      is_recovery_discount: discountAccepted,
                      has_offer: hasOffer,
                      currency,
                      gateway: provider,
                      destination: vipPlan.playbackId
                        ? "vip_video"
                        : vipPlan.exclusivePath
                          ? "exclusive_path"
                          : "direct_checkout",
                      has_video: !!vipPlan.playbackId,
                      has_exclusive_path: !!vipPlan.exclusivePath,
                    });
                    if (vipPlan.playbackId) {
                      setShowVipVideo(true);
                      trackVipVideoOpenedPostHog(flowName, vipPlan.playbackId, country);
                    } else if (vipPlan.exclusivePath) {
                      navigate(vipPlan.exclusivePath);
                    } else {
                      setCheckoutPlan("vip");
                      checkoutOpenSourceRef.current = "direct_button";
                      checkoutPanelOpenTimeRef.current = Date.now();
                      trackCheckoutPanelOpenedPostHog(
                        "vip",
                        "direct_button",
                        price,
                        currency,
                        flowName,
                        country,
                      );
                      setShowCustomCheckout(true);
                    }
                  }}
                  disabled={vipPlan.isDisabled}
                  className={`w-full min-h-[44px] sm:min-h-0 py-2.5 sm:py-3 md:py-3.5 px-3 sm:px-4 md:px-5 rounded-xl font-bold text-sm md:text-base leading-tight transition-all ${
                    vipPlan.isDisabled
                      ? "bg-gray-700/30 text-gray-500 cursor-not-allowed opacity-50 shadow-none"
                      : isImmediateDiscount
                        ? "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-black shadow-[0_0_24px_rgba(245,158,11,0.45)] hover:from-amber-300 hover:via-yellow-300 hover:to-amber-400 hover:shadow-[0_0_32px_rgba(251,191,36,0.5)] active:scale-[0.98] border border-yellow-200/30"
                        : discountAccepted
                          ? "bg-amber-500/90 text-black hover:bg-amber-500 hover:shadow-xl active:scale-[0.98] shadow-lg"
                          : "bg-amber-500/80 text-white hover:bg-amber-500/90 hover:shadow-xl active:scale-[0.98] shadow-lg"
                  }`}
                >
                  {vipPlan.isDisabled ? L.vipContactForAvailability : L.joinVip}
                </button>
              </div>
            )}

            {/* WhatsApp: mismo stack vertical que las cards */}
            {showWhatsappBroadcast && (
              <div className="w-full pt-2 sm:pt-3 border-t border-white/10">
                <div className="w-full max-w-none mx-auto px-0">
                  {/* Header con icono de WhatsApp */}
                  <div className="flex items-center justify-center gap-2 mb-2.5 sm:mb-3 md:mb-4">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 text-green-500/70"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <span className="text-xs md:text-sm font-medium text-white/60">
                      {L.whatsappTitle}
                    </span>
                  </div>

                {/* CTA sin precio */}
                <div className="bg-white/[0.03] border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-3.5 md:p-4 backdrop-blur-sm w-full min-w-0">
                  {/* CTA Button */}
                  <button
                    onClick={handleWhatsappCheckout}
                    className="w-full min-h-[44px] sm:min-h-0 bg-green-600/85 hover:bg-green-600 text-white font-medium text-xs sm:text-sm py-2.5 sm:py-2.5 md:py-3 px-4 rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-3.5 h-3.5 md:w-4 md:h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {L.whatsappSubscribe}
                  </button>

                  <p className="text-[10px] sm:text-[11px] md:text-xs text-center mt-2 sm:mt-2.5 text-gray-500 leading-snug px-1">
                    {L.whatsappSecurePayment} Stripe • {L.whatsappCancelAnytime}
                  </p>
                </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer info */}
          <p className={`text-xs md:text-sm mt-5 sm:mt-6 md:mt-8 ${
            isImmediateDiscount ? "text-gray-400" : "text-gray-500"
          }`}>
            {checkoutTypeLabel} · {gatewayLabel}
          </p>

          {/* Trust badges */}
          <div className={`mt-2 md:mt-3 flex items-center justify-center gap-4 md:gap-5 text-xs md:text-sm ${
            isImmediateDiscount ? "text-gray-400" : "text-gray-500"
          }`}>
            <span className="inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {L.footerSecure}
            </span>
            <span className={`w-1 h-1 rounded-full ${
              isImmediateDiscount ? "bg-gray-500" : "bg-gray-600"
            }`} />
            <span className="inline-flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {L.footerInstant}
            </span>
          </div>

          {/* Términos y Condiciones / Terms and Conditions */}
          <div className="mt-3 md:mt-4">
            <Link
              to="/terminos-y-condiciones"
              className={`text-[10px] md:text-xs transition-colors underline ${
                isImmediateDiscount 
                  ? "text-gray-500 hover:text-gray-300 decoration-gray-600 hover:decoration-gray-400"
                  : "text-gray-600 hover:text-gray-400 decoration-gray-700 hover:decoration-gray-500"
              }`}
            >
              {L.termsAndConditions}
            </Link>
          </div>

          </div>
        </div>
      </div>
    </div>
  );
}
